import { classNames, elementAttributes } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'

/**
 * Create a React Toggle component
 *
 * @param {import('./interfaces').ToggleProps} props
 * @returns {JSX.Element}
 */
function Toggle ({
  name,
  id,
  className,
  renderFn,
  tabIndex,
  defaultPressed,
  onChange,
  disabled,
  pressed: propPressed,
  ...props
}) {
  const [focused, setFocused] = useState(false)
  const [pressed, setPressed] = useState(
    propPressed === undefined ? defaultPressed : propPressed
  )
  const disabledHandler = disabled ? null : true
  const isControlled = propPressed != null

  /**
   * @type {import('react').ChangeEventHandler<HTMLInputElement>}
   */
  const handleChange = useCallback(
    (event) => {
      const {
        target: { checked },
        type,
        key
      } = event
      let value = checked
      if (type === 'keydown') {
        if (key !== 'Enter') return
        event.preventDefault()
        value = !checked
      }
      if (!isControlled) setPressed(value)

      onChange(event, value)
    },
    [isControlled]
  )
  const handleFocusChange = useCallback(({ type }) => {
    setFocused(type === 'focus')
  }, [])

  const containerProps = elementAttributes(props, {
    className: classNames('zb-toggle', className, {
      'zb-toggle-is-pressed': pressed,
      'zb-toggle-is-focused': focused,
      'zb-toggle-is-disabled': disabled
    }),
    onFocus: disabledHandler && handleFocusChange,
    onBlur: disabledHandler && handleFocusChange
  })

  useEffect(componentWillReceiveProps, [propPressed])

  return (
    <label {...containerProps}>
      {renderFn({ isPressed: pressed })}
      <input
        type='checkbox'
        name={name}
        id={id}
        checked={pressed}
        onChange={disabledHandler && handleChange}
        onKeyDown={disabledHandler && handleChange}
        className='zb-checkbox-input'
        tabIndex={tabIndex}
        disabled={disabled}
      />
    </label>
  )

  function componentWillReceiveProps () {
    if (propPressed !== undefined) {
      setPressed(propPressed)
    }
  }
}

function noop () {}

Toggle.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  pressed: PropTypes.bool,
  defaultPressed: PropTypes.bool,
  disabled: PropTypes.bool,
  tabIndex: PropTypes.string,
  renderFn: PropTypes.func,
  onChange: PropTypes.func
}

Toggle.defaultProps = {
  defaultPressed: false,
  pressed: undefined,
  disabled: false,
  tabIndex: '0',
  renderFn: noop,
  onChange: noop
}

export default Toggle
